<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import Modal from '$lib/components/ui/Modal.svelte';
  import { boardApi, noWorkApi } from '$lib/api/board';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import NoWorkModal from './NoWorkModal.svelte';
  import { t } from '$lib/i18n';
  import type { BoardCard } from '$lib/types';
  import { myDayOpenCards, myDayReviewChanges } from '$lib/utils/myDay';
  import { boardToday } from '$lib/utils/board';
  import { route } from '$lib/state/route.svelte';

  const {
    kind,
    cards,
    loading = false,
    failed = false,
    onRetry = () => {},
    onClose,
    onMutated
  }: {
    kind: 'in' | 'out';
    cards: BoardCard[];
    loading?: boolean;
    failed?: boolean;
    onRetry?: () => void;
    onClose: () => void;
    onMutated: () => void;
  } = $props();

  const doingCards = $derived(cards.filter((c) => c.col === 'doing'));
  const reviewCards = $derived(myDayOpenCards(cards, boardToday()));
  const todoCards = $derived(cards.filter((c) => c.col === 'todo'));
  const ownCards = $derived(cards.filter((c) => c.col === 'todo' || c.col === 'blocked'));
  const hasWorkCards = $derived(reviewCards.length > 0);

  let selected = $state<Set<number>>(new Set());
  let selectionReady = $state(false);
  let newTitle = $state('');
  let saving = $state(false);
  let error = $state('');
  let showNoWork = $state(false);

  const noWorkQuery = createQuery(() => ({
    queryKey: ['no-work-days'],
    queryFn: () => noWorkApi.list()
  }));

  $effect(() => {
    if (kind === 'in' && hasWorkCards && !selectionReady) {
      selected = new Set(doingCards.map((c) => c.id));
      selectionReady = true;
    }
  });

  function toggle(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selected = next;
  }

  const canConfirmIn = $derived(doingCards.length > 0 || selected.size > 0);

  async function confirmPunchIn() {
    if (!canConfirmIn) return;
    saving = true;
    error = '';
    try {
      for (const change of myDayReviewChanges(cards, selected, 'in')) {
        await boardApi.update(change.id, { col: change.col });
      }
      toast.success(t('punch.board.toast.confirmed'));
      onClose();
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      onMutated();
      saving = false;
    }
  }

  async function markSelectedDone() {
    if (selected.size === 0) {
      onClose();
      return;
    }
    saving = true;
    error = '';
    try {
      for (const change of myDayReviewChanges(cards, selected, 'out')) {
        await boardApi.update(change.id, { col: change.col });
      }
      toast.success(t('punch.board.toast.markedDone'));
      onClose();
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      onMutated();
      saving = false;
    }
  }

  async function createWork() {
    if (!newTitle.trim()) {
      error = t('board.error.titleRequired');
      return;
    }
    saving = true;
    error = '';
    try {
      await boardApi.create({ title: newTitle.trim(), col: 'doing' });
      onMutated();
      toast.success(t('board.toast.created'));
      onClose();
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      saving = false;
    }
  }

  const title = $derived(
    kind === 'out'
      ? t('myDay.reviewTitle')
      : hasWorkCards
        ? t('punch.board.pickWorkTitle')
        : t('punch.board.emptyTitle')
  );
</script>

{#if loading || failed}
  <Modal {title} {onClose}>
    <p role="status">{loading ? t('common.loading') : t('myDay.loadError')}</p>
    {#if failed}<button type="button" class="btn-secondary mt-3" onclick={onRetry}>{t('common.retry')}</button>{/if}
    <button type="button" class="btn-secondary mt-3" onclick={onClose}>{t('myDay.later')}</button>
  </Modal>
{:else if showNoWork}
  <NoWorkModal
    {ownCards}
    recentDays={noWorkQuery.data ?? []}
    onClose={() => {
      showNoWork = false;
    }}
    onSuccess={() => {
      toast.success(t('board.toast.logged'));
      onClose();
    }}
  />
{:else if kind === 'in' && hasWorkCards}
  <Modal {title} {onClose} closable={!saving}>
    <div class="space-y-4">
      <p class="text-[13px] text-dark-gray">{t('myDay.planHint')}</p>

      {#if doingCards.length > 0}
        <div>
          <p class="mb-2 text-[11px] font-bold uppercase tracking-[.12em] text-dark-gray">
            {t('punch.board.groupOngoing')}
          </p>
          <div class="flex flex-wrap gap-2">
            {#each doingCards as card (card.id)}
              <button
                type="button"
                class="tym-chip !min-h-[44px] {selected.has(card.id) ? 'active' : ''}"
                aria-pressed={selected.has(card.id)}
                disabled={saving}
                onclick={() => toggle(card.id)}
              >
                {card.title}
              </button>
            {/each}
          </div>
        </div>
      {/if}

      {#if todoCards.length > 0}
        <div>
          <p class="mb-2 text-[11px] font-bold uppercase tracking-[.12em] text-dark-gray">
            {t('punch.board.groupAvailable')}
          </p>
          <div class="flex flex-wrap gap-2">
            {#each todoCards as card (card.id)}
              <button
                type="button"
                class="tym-chip !min-h-[44px] {selected.has(card.id) ? 'active' : ''}"
                aria-pressed={selected.has(card.id)}
                disabled={saving}
                onclick={() => toggle(card.id)}
              >
                {card.title}
              </button>
            {/each}
          </div>
        </div>
      {/if}

      {#if ownCards.some((card) => card.col === 'blocked')}
        <div>
          <p class="mb-2 text-sm font-semibold text-navy">{t('board.column.blocked')}</p>
          {#each ownCards.filter((card) => card.col === 'blocked') as card (card.id)}
            <button type="button" class="btn-secondary mb-2 min-h-[44px] w-full break-words text-left" disabled={saving} onclick={() => { onClose(); route.push({ tab: 'board', sub: 'card', cardId: card.id, update: true }); }}>{card.title} · {t('myDay.help')}</button>
          {/each}
        </div>
      {/if}

      {#if error}<div class="tym-error-state">{error}</div>{/if}

      <div class="flex flex-wrap justify-end gap-2 pt-1">
        <button type="button" class="btn-secondary" disabled={saving} onclick={onClose}>{t('myDay.later')}</button>
        <button
          type="button"
          class="btn-primary"
          disabled={saving || !canConfirmIn}
          onclick={confirmPunchIn}
        >
          {saving ? t('common.submitting') : t('punch.board.confirm')}
        </button>
      </div>
    </div>
  </Modal>
{:else if kind === 'in'}
  <Modal {title} {onClose} closable={!saving}>
    <div class="space-y-4">
      <p class="text-[13px] text-dark-gray">{t('punch.board.emptyHint')}</p>
      <div>
        <div class="label">{t('board.title')}</div>
        <input type="text" class="input" aria-label={t('board.title')} bind:value={newTitle} />
      </div>

      {#if error}<div class="tym-error-state">{error}</div>{/if}

      <div class="flex flex-wrap justify-end gap-2 pt-1">
        <button type="button" class="btn-secondary" disabled={saving} onclick={onClose}>{t('myDay.later')}</button>
        <button type="button" class="btn-secondary" disabled={saving} onclick={() => (showNoWork = true)}>
          {t('board.noWorkToday')}
        </button>
        <button type="button" class="btn-primary" disabled={saving} onclick={createWork}>
          {saving ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </div>
  </Modal>
{:else}
  <Modal {title} {onClose} closable={!saving}>
    <div class="space-y-4">
      <p class="text-[13px] text-dark-gray">{t('myDay.reviewHint')}</p>
      <ul class="space-y-2">
        {#each reviewCards as card (card.id)}
          <li>
            <label class="flex min-h-[44px] cursor-pointer items-start gap-2.5 rounded-[10px] border border-toyama-cloudy-gray bg-surface px-3 py-2.5">
              <input
                type="checkbox"
                class="mt-0.5"
                disabled={saving}
                checked={selected.has(card.id)}
                onchange={() => toggle(card.id)}
              />
              <span class="min-w-0 break-words text-[13px] font-medium text-navy">{card.title}<span class="mt-1 block text-xs text-dark-gray">{t(`board.column.${card.col}`)}</span></span>
            </label>
          </li>
        {:else}<li class="text-sm text-dark-gray">{t('dashboard.noOpenTasks')}</li>{/each}
      </ul>

      {#if error}<div class="tym-error-state">{error}</div>{/if}

      <div class="flex flex-wrap justify-end gap-2 pt-1">
        <button type="button" class="btn-secondary" disabled={saving} onclick={onClose}>{t('myDay.carryForward')}</button>
        <button type="button" class="btn-primary" disabled={saving || selected.size === 0} onclick={markSelectedDone}>
          {saving ? t('common.submitting') : t('myDay.completeSelected', { n: selected.size })}
        </button>
      </div>
    </div>
  </Modal>
{/if}
